import type { ReorderPolicy, VariantPolicyRow } from "../types";
import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export interface VariantPolicyUpsert {
  reorder_point?: number;
  days_of_supply_target?: number | null;
  lead_time_days?: number | null;
  is_active?: boolean;
  service_level_pct?: number | null;
  min_order_qty?: number | null;
  replenish_days?: number | null;
}

export interface ReorderPolicyCreate {
  shopify_variant_id: StockyPublicBigIntId;
  reorder_point?: number;
  days_of_supply_target?: number | null;
  lead_time_days?: number | null;
  is_active?: boolean;
  service_level_pct?: number | null;
  min_order_qty?: number | null;
  replenish_days?: number | null;
}

export interface ReorderPolicyUpdate {
  reorder_point?: number;
  days_of_supply_target?: number | null;
  lead_time_days?: number | null;
  is_active?: boolean;
  service_level_pct?: number | null;
  min_order_qty?: number | null;
  replenish_days?: number | null;
}

function canonicalPolicy(policy: ReorderPolicy): ReorderPolicy {
  return {
    ...policy,
    id: canonicalStockyPublicBigIntId(policy.id),
    shopify_variant_id: canonicalStockyPublicBigIntId(
      policy.shopify_variant_id,
    ),
  };
}

function canonicalNullableId(value: unknown): string | null {
  return value === null ? null : canonicalStockyPublicBigIntId(value);
}

function canonicalVariantPolicyRow(row: VariantPolicyRow): VariantPolicyRow {
  return {
    ...row,
    variant_id: canonicalStockyPublicBigIntId(row.variant_id),
    shopify_variant_id: canonicalStockyPublicBigIntId(
      row.shopify_variant_id,
    ),
    supplier_id: canonicalNullableId(row.supplier_id),
    policy_id: canonicalNullableId(row.policy_id),
  };
}

export async function listPolicies(): Promise<ReorderPolicy[]> {
  const policies = await apiFetch<ReorderPolicy[]>("/api/reorder-policies");
  return policies.map(canonicalPolicy);
}

export async function listVariantPolicies(): Promise<VariantPolicyRow[]> {
  const rows = await apiFetch<VariantPolicyRow[]>(
    "/api/reorder-policies/variants",
  );
  return rows.map(canonicalVariantPolicyRow);
}

export async function upsertVariantPolicy(
  shopifyVariantId: StockyPublicBigIntId,
  body: VariantPolicyUpsert,
): Promise<ReorderPolicy> {
  const canonicalId = canonicalStockyPublicBigIntId(shopifyVariantId);
  const policy = await apiFetch<ReorderPolicy>(
    `/api/reorder-policies/variants/${encodeURIComponent(canonicalId)}`,
    { method: "PUT", body },
  );
  return canonicalPolicy(policy);
}

export function bulkUpdateVariantLeadTimes(
  shopifyVariantIds: StockyPublicBigIntId[],
  leadTimeDays: number,
): Promise<{ updated: number }> {
  return apiFetch<{ updated: number }>(
    "/api/reorder-policies/variants/bulk-lead-time",
    {
      method: "PUT",
      body: {
        shopify_variant_ids: shopifyVariantIds.map(
          canonicalStockyPublicBigIntId,
        ),
        lead_time_days: leadTimeDays,
      },
    },
  );
}

export async function createPolicy(
  body: ReorderPolicyCreate,
): Promise<ReorderPolicy> {
  const policy = await apiFetch<ReorderPolicy>("/api/reorder-policies", {
    method: "POST",
    body: {
      ...body,
      shopify_variant_id: canonicalStockyPublicBigIntId(
        body.shopify_variant_id,
      ),
    },
  });
  return canonicalPolicy(policy);
}

export async function updatePolicy(
  id: StockyPublicBigIntId,
  body: ReorderPolicyUpdate,
): Promise<ReorderPolicy> {
  const canonicalId = canonicalStockyPublicBigIntId(id);
  const policy = await apiFetch<ReorderPolicy>(
    `/api/reorder-policies/${encodeURIComponent(canonicalId)}`,
    {
      method: "PATCH",
      body,
    },
  );
  return canonicalPolicy(policy);
}

export async function deletePolicy(
  id: StockyPublicBigIntId,
): Promise<{ deleted: string }> {
  const canonicalId = canonicalStockyPublicBigIntId(id);
  const response = await apiFetch<{ deleted: unknown }>(
    `/api/reorder-policies/${encodeURIComponent(canonicalId)}`,
    { method: "DELETE" },
  );
  return { deleted: canonicalStockyPublicBigIntId(response.deleted) };
}

/** 入荷実績から出した仕入先別リードタイムの提示（採用するまで何も変わらない）。 */
export interface SupplierLeadTimeSuggestion {
  supplier_id: string;
  supplier_name: string | null;
  avg_lead_time_days: number;
  sample_size: number;
  fillable_variant_count: number;
}

export interface LeadTimeApplyResult {
  applied_lead_time_days: number;
  updated: number;
  kept_existing: number;
  inert: number;
}

type SupplierLeadTimeSuggestionWire = Omit<
  SupplierLeadTimeSuggestion,
  "supplier_id"
> & { supplier_id: unknown };

export async function listLeadTimeSuggestions(): Promise<
  SupplierLeadTimeSuggestion[]
> {
  const body = await apiFetch<{
    suggestions: SupplierLeadTimeSuggestionWire[];
  }>(
    "/api/reorder-policies/lead-time-suggestions",
  );
  return body.suggestions.map((suggestion) => ({
    ...suggestion,
    supplier_id: canonicalStockyPublicBigIntId(suggestion.supplier_id),
  }));
}

/**
 * 採用。書き込む日数はサーバが実測から引き直すので、ここでは渡さない
 * （画面に出ていた数字と実際に書かれる数字を必ず一致させるため）。
 */
export function applyLeadTimeSuggestion(
  supplierId: StockyPublicBigIntId,
): Promise<LeadTimeApplyResult> {
  const canonicalId = canonicalStockyPublicBigIntId(supplierId);
  return apiFetch<LeadTimeApplyResult>(
    `/api/reorder-policies/lead-time-suggestions/${encodeURIComponent(canonicalId)}/apply`,
    { method: "POST" },
  );
}

/**
 * 発注書の履歴から出した仕入先別の発注間隔の提示。
 *
 * 🔴 リードタイムと違い日次では自動適用されない＝押すまで補充日数は入らない
 * （こちらは発注の【時期】ではなく【数量】を増やす＝現金が在庫に変わる）。
 */
export interface SupplierReplenishSuggestion {
  supplier_id: string;
  supplier_name: string | null;
  interval_days: number;
  sample_size: number;
  fillable_variant_count: number;
}

export interface ReplenishApplyResult {
  applied_replenish_days: number;
  updated: number;
  kept_existing: number;
}

type SupplierReplenishSuggestionWire = Omit<
  SupplierReplenishSuggestion,
  "supplier_id"
> & { supplier_id: unknown };

export async function listReplenishSuggestions(): Promise<
  SupplierReplenishSuggestion[]
> {
  const body = await apiFetch<{
    suggestions: SupplierReplenishSuggestionWire[];
  }>(
    "/api/reorder-policies/replenish-suggestions",
  );
  return body.suggestions.map((suggestion) => ({
    ...suggestion,
    supplier_id: canonicalStockyPublicBigIntId(suggestion.supplier_id),
  }));
}

/** 採用。書き込む日数はサーバが実測から引き直すので、ここでは渡さない。 */
export function applyReplenishSuggestion(
  supplierId: StockyPublicBigIntId,
): Promise<ReplenishApplyResult> {
  const canonicalId = canonicalStockyPublicBigIntId(supplierId);
  return apiFetch<ReplenishApplyResult>(
    `/api/reorder-policies/replenish-suggestions/${encodeURIComponent(canonicalId)}/apply`,
    { method: "POST" },
  );
}
